import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Chip,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  CircularProgress,
  Tooltip,
  TablePagination,
  keyframes,
  Drawer,
  Divider,
} from "@mui/material";
import { Close, Refresh, Settings, Visibility } from "@mui/icons-material";

import { APP_API_V1_BASE } from "../../../config/api";
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const pulse = keyframes`0%, 100% { opacity: 1; } 50% { opacity: 0.4; }`;
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const getSeverityStyles = (level) => {
  switch (level) {
    case "error":
      return {
        bg: "#FEF2F2",
        color: "#B91C1C",
        border: "#FECACA",
        dot: "#EF4444",
        accent: "#EF4444",
      };
    case "warn":
      return {
        bg: "#FFF7ED",
        color: "#C2410C",
        border: "#FED7AA",
        dot: "#F97316",
        accent: "#F97316",
      };
    case "info":
      return {
        bg: "#EFF6FF",
        color: "#1D4ED8",
        border: "#BFDBFE",
        dot: "#3B82F6",
        accent: "#3B82F6",
      };
    default:
      return {
        bg: "#F8FAFC",
        color: "#475569",
        border: "#E2E8F0",
        dot: "#94A3B8",
        accent: "#CBD5E1",
      };
  }
};

const getAvatarColor = (char) => {
  const colors = [
    { bg: "#E0E7FF", color: "#3730A3" },
    { bg: "#D1FAE5", color: "#065F46" },
    { bg: "#FEF3C7", color: "#92400E" },
    { bg: "#FCE7F3", color: "#9D174D" },
  ];
  return colors[char.charCodeAt(0) % colors.length];
};

export default function DeviceLogsTable({ deviceId, isMobile }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [level, setLevel] = useState("");
  const [page, setPage] = useState(0);
  const [lastRefreshed, setLastRefreshed] = useState(new Date());
  const [selectedLog, setSelectedLog] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const handleRowClick = (log) => {
    setSelectedLog(log);
    setDrawerOpen(true);
  };
  const pageSize = 50;

  const fetchLogs = async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const params = { limit: pageSize, offset: page * pageSize };
      if (level) params.level = level;
      const res = await axios.get(
        `${APP_API_V1_BASE}/devices/${deviceId}/logs`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        },
      );
      setLogs(res.data.logs || []);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error("Failed to fetch device logs", err);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [deviceId, level, page]);

  const thSx = {
    fontSize: 10.5,
    fontWeight: 700,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    color: "text.secondary",
    bgcolor: "#F8FAFC",
    borderBottom: "1px solid",
    borderColor: "divider",
    py: 1,
    px: 2,
    whiteSpace: "nowrap",
  };

  const tdSx = {
    fontSize: 13,
    borderBottom: "1px solid",
    borderColor: "#F1F5F9",
    py: 1.2,
    px: 2,
  };

  function DetailRow({ label, value, mono = false, chip = null }) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          py: 1.4,
          borderBottom: "0.5px solid",
          borderColor: "divider",
          "&:last-child": { borderBottom: "none" },
        }}
      >
        <Typography
          sx={{
            fontSize: 11.5,
            color: "text.secondary",
            fontWeight: 500,
            minWidth: 90,
            flexShrink: 0,
            pt: 0.1,
          }}
        >
          {label}
        </Typography>
        {chip ?? (
          <Typography
            sx={{
              fontSize: 12,
              color: "text.primary",
              fontWeight: 500,
              textAlign: "right",
              wordBreak: "break-all",
              fontFamily: mono
                ? "'JetBrains Mono','Fira Code',monospace"
                : "inherit",
            }}
          >
            {value || "—"}
          </Typography>
        )}
      </Box>
    );
  }
  const sev = selectedLog ? getSeverityStyles(selectedLog.level) : null;
  const avatarChar = selectedLog
    ? (selectedLog.user || "D").charAt(0).toUpperCase()
    : "D";
  const avatarColors = getAvatarColor(avatarChar);
  return (
    <>
      <Box
        sx={{
          width: "100%",
          borderRadius: "12px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
          boxShadow: "0 1px 4px 0 rgba(16,24,40,0.06)",
          animation: `${fadeSlideIn} 0.3s ease`,
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: 2.5,
            py: 1.6,
            display: "flex",
            flexWrap: isMobile ? "wrap" : "nowrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "#FAFAFA",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{ position: "relative", width: 8, height: 8, flexShrink: 0 }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: "#3B6D11",
                  opacity: 0.2,
                  animation: `${pulse} 2s ease-in-out infinite`,
                  transform: "scale(2)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  bgcolor: "#3B6D11",
                }}
              />
            </Box>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: "text.primary",
                letterSpacing: "-0.01em",
              }}
            >
              Device Activity
            </Typography>
            <Chip label={`${logs.length} events`} size="small" />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isMobile && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "6px",
                  px: 1,
                  py: 0.4,
                }}
              >
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: "#22C55E",
                  }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: 11 }}
                >
                  Updated{" "}
                  {lastRefreshed.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>
            )}

            <Select
              size="small"
              value={level}
              displayEmpty
              onChange={(e) => {
                setPage(0);
                setLevel(e.target.value);
              }}
              sx={{
                fontSize: 11,
                height: 30,
                borderRadius: "8px",
                bgcolor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "divider",
                  borderWidth: "1px",
                },
                "& .MuiSelect-select": { py: 0 },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 12 }}>
                All levels
              </MenuItem>
              <MenuItem value="info" sx={{ fontSize: 12 }}>
                Info
              </MenuItem>
              <MenuItem value="warn" sx={{ fontSize: 12 }}>
                Warning
              </MenuItem>
              <MenuItem value="error" sx={{ fontSize: 12 }}>
                Error
              </MenuItem>
            </Select>

            <Tooltip title="Refresh logs">
              <IconButton
                size="small"
                onClick={fetchLogs}
                disabled={loading}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  width: 30,
                  height: 30,
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#F1F5F9" },
                  "& svg": {
                    fontSize: 15,
                    animation: loading
                      ? `${spin} 0.8s linear infinite`
                      : "none",
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* ── Table ── */}
        <TableContainer
          sx={{
            overflowX: "auto",
            maxWidth: "100%",
            width: isMobile ? "21.875rem" : "auto",
          }}
        >
          <Table sx={{ minWidth: isMobile ? "15.625rem" : "40.625rem" }}>
            <TableHead>
              <TableRow>
                <TableCell>
                  <b>TIMESTAMP</b>
                </TableCell>
                <TableCell>
                  <b>TYPE</b>
                </TableCell>
                <TableCell>
                  <b>USER / DEVICE</b>
                </TableCell>
                <TableCell>
                  <b>ACTION</b>
                </TableCell>
                <TableCell>
                  <b>TARGET</b>
                </TableCell>
                <TableCell>
                  <b>SEVERITY</b>
                </TableCell>
                <TableCell>
                  <b>IP ADDRESS</b>
                </TableCell>
                <TableCell align="center">
                  <b>DETAILS</b>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{ py: 6, border: "none" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <CircularProgress
                        size={20}
                        thickness={4}
                        sx={{ color: "#3B6D11" }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: 12 }}
                      >
                        Fetching device logs…
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading && logs.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    align="center"
                    sx={{ py: 6, border: "none" }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "12px",
                          bgcolor: "#F1F5F9",
                          border: "1px solid #E2E8F0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 0.5,
                          fontSize: 18,
                        }}
                      >
                        🖥
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        No logs found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}

              {!loading &&
                logs.map((log) => {
                  const sev = getSeverityStyles(log.level);
                  const avatarChar = (log.user || "D").charAt(0).toUpperCase();
                  const avatarColors = getAvatarColor(avatarChar);

                  return (
                    <TableRow
                      key={log.id}
                      sx={{
                        cursor: "pointer",
                        transition: "background 0.15s ease",
                        "&:hover": { bgcolor: "#F8FAFC" },
                        "&:hover td": { borderColor: "#E2E8F0" },
                        "&:last-child td": { border: "none" },
                      }}
                    >
                      {/* Timestamp with severity accent bar */}
                      <TableCell sx={tdSx}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 2.5,
                              height: 28,
                              bgcolor: sev.accent,
                              borderRadius: 0,
                              flexShrink: 0,
                            }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: "text.primary",
                                lineHeight: 1.3,
                              }}
                            >
                              {new Date(log.created_at).toLocaleDateString(
                                undefined,
                                {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                },
                              )}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: 10.5,
                                color: "text.disabled",
                                lineHeight: 1.3,
                              }}
                            >
                              {new Date(log.created_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                                second: "2-digit",
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      {/* Type */}
                      <TableCell sx={tdSx}>
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          {log.type || "SYSTEM"}
                        </Typography>
                      </TableCell>

                      {/* User / Device */}
                      <TableCell sx={tdSx}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: "6px",
                              bgcolor: avatarColors.bg,
                              color: avatarColors.color,
                              fontSize: 10,
                              fontWeight: 700,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {avatarChar}
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 12,
                              fontWeight: 500,
                              color: "text.primary",
                            }}
                          >
                            {log.user || "Device"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Action */}
                      <TableCell sx={tdSx}>
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          {log.action || "—"}
                        </Typography>
                      </TableCell>

                      {/* Target */}
                      <TableCell sx={tdSx}>
                        <Typography
                          sx={{ fontSize: 12, color: "text.secondary" }}
                        >
                          {log.target || "—"}
                        </Typography>
                      </TableCell>

                      {/* Severity */}
                      <TableCell sx={tdSx}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.6,
                            fontSize: 11,
                            fontWeight: 700,
                            px: 1,
                            py: 0.35,
                            borderRadius: "5px",
                            background: sev.bg,
                            color: sev.color,
                            border: `1px solid ${sev.border}`,
                            letterSpacing: "0.04em",
                          }}
                        >
                          <Box
                            sx={{
                              width: 5,
                              height: 5,
                              borderRadius: "50%",
                              bgcolor: sev.dot,
                              flexShrink: 0,
                            }}
                          />
                          {log.level || "—"}
                        </Box>
                      </TableCell>

                      {/* IP */}
                      <TableCell sx={tdSx}>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            bgcolor: "#F8FAFC",
                            border: "0.5px solid #E2E8F0",
                            borderRadius: "5px",
                            px: 1,
                            py: 0.3,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily:
                                "'JetBrains Mono', 'Fira Code', monospace",
                              fontSize: 11,
                              color: "text.secondary",
                            }}
                          >
                            {log.ip || "—"}
                          </Typography>
                        </Box>
                      </TableCell>

                      {/* Details */}
                      <TableCell sx={{ ...tdSx, textAlign: "center" }}>
                        <Tooltip title="View details">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(log);
                            }}
                            sx={{
                              border: "1px solid",
                              borderColor: "divider",
                              borderRadius: "6px",
                              width: 26,
                              height: 26,
                              bgcolor: "white",
                              "&:hover": {
                                bgcolor: "#F1F5F9",
                                borderColor: "#CBD5E1",
                              },
                              "& svg": { fontSize: 13 },
                            }}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── Pagination ── */}
        {logs.length > 0 && (
          <TablePagination
            component="div"
            count={-1}
            page={page}
            rowsPerPage={pageSize}
            rowsPerPageOptions={[50]}
            onPageChange={(_, newPage) => setPage(newPage)}
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              bgcolor: "#F8FAFC",
              "& .MuiTablePagination-toolbar": { minHeight: 44 },
              "& .MuiTablePagination-displayedRows": {
                fontSize: 11,
                color: "text.secondary",
              },
              "& .MuiTablePagination-selectLabel": {
                fontSize: 11,
                color: "text.secondary",
              },
            }}
          />
        )}
      </Box>
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100vw", sm: 380 },
            borderLeft: "1px solid",
            borderColor: "divider",
            boxShadow: "-4px 0 24px rgba(0,0,0,0.07)",
          },
        }}
      >
        {selectedLog && sev && (
          <Box
            sx={{ display: "flex", flexDirection: "column", height: "100%" }}
          >
            {/* Drawer Header */}
            <Box
              sx={{
                px: 2.5,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "#FAFAFA",
                flexShrink: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    borderRadius: "10px",
                    bgcolor: sev.bg,
                    border: `1px solid ${sev.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: sev.dot,
                    }}
                  />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "text.primary",
                      letterSpacing: "-0.01em",
                    }}
                  >
                    Device Details
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "text.secondary",
                      fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    }}
                  >
                    #{selectedLog.id || "—"}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={() => setDrawerOpen(false)}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "8px",
                  width: 30,
                  height: 30,
                  bgcolor: "white",
                  "&:hover": { bgcolor: "#F1F5F9" },
                }}
              >
                <Close sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>

            {/* Severity Banner */}
            <Box
              sx={{
                mx: 2.5,
                mt: 2,
                px: 2,
                py: 1.2,
                borderRadius: "8px",
                bgcolor: sev.bg,
                border: `1px solid ${sev.border}`,
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  bgcolor: sev.dot,
                  flexShrink: 0,
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: sev.color,
                  letterSpacing: "0.04em",
                  textTransform: "uppercase",
                }}
              >
                {selectedLog.level || "unknown"} severity
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Typography sx={{ fontSize: 11, color: sev.color, opacity: 0.8 }}>
                {new Date(selectedLog.created_at).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                })}
              </Typography>
            </Box>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: "auto", px: 2.5, pt: 2.5, pb: 4 }}>
              {/* Section: Event */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 0.8,
                }}
              >
                Event
              </Typography>
              <Box
                sx={{
                  bgcolor: "#FAFAFA",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  px: 2,
                  mb: 2.5,
                }}
              >
                <DetailRow
                  label="Timestamp"
                  value={new Date(selectedLog.created_at).toLocaleString()}
                />
                <DetailRow label="Type" value={selectedLog.type || "SYSTEM"} />
                <DetailRow label="Action" value={selectedLog.action || "—"} />
                <DetailRow label="Target" value={selectedLog.target || "—"} />
              </Box>

              {/* Section: Actor */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 0.8,
                }}
              >
                Actor
              </Typography>
              <Box
                sx={{
                  bgcolor: "#FAFAFA",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  px: 2,
                  mb: 2.5,
                }}
              >
                <DetailRow
                  label="User / Device"
                  chip={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "5px",
                          bgcolor: avatarColors.bg,
                          color: avatarColors.color,
                          fontSize: 9,
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {avatarChar}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: "text.primary",
                        }}
                      >
                        {selectedLog.user || "Device"}
                      </Typography>
                    </Box>
                  }
                />
                <DetailRow
                  label="IP Address"
                  value={selectedLog.ip || "—"}
                  mono
                />
              </Box>

              {/* Section: Metadata (if present) */}
              {selectedLog.metadata && (
                <>
                  <Typography
                    sx={{
                      fontSize: 10.5,
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      color: "text.secondary",
                      mb: 0.8,
                    }}
                  >
                    Metadata
                  </Typography>
                  <Box
                    sx={{
                      bgcolor: "#F8FAFC",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "10px",
                      p: 1.5,
                      mb: 2.5,
                    }}
                  >
                    <Typography
                      component="pre"
                      sx={{
                        fontFamily: "'JetBrains Mono','Fira Code',monospace",
                        fontSize: 11.5,
                        color: "text.secondary",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        margin: 0,
                      }}
                    >
                      {JSON.stringify(selectedLog.metadata, null, 2)}
                    </Typography>
                  </Box>
                </>
              )}

              {/* Section: Raw */}
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  mb: 0.8,
                }}
              >
                Raw Log
              </Typography>
              <Box
                sx={{
                  bgcolor: "#F8FAFC",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "10px",
                  p: 1.5,
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    fontFamily: "'JetBrains Mono','Fira Code',monospace",
                    fontSize: 11,
                    color: "text.secondary",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    margin: 0,
                  }}
                >
                  {JSON.stringify(selectedLog, null, 2)}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
}
